// parentId 로 저장된 WBS 계층을 화면이 쓸 트리로 만든다.
//
// 지금까지 계층이 들어오면 부모 행은 자기 날짜만 그렸다. 부모가 하루로
// 잡혀 있고 자식이 한 달에 걸쳐 있으면 묶음 막대가 없는 것과 같아, 접어도
// 펴도 타임라인이 조각 막대로만 보인다. 자식 기간의 min~max 로 넓히고
// 진행률은 자식 평균으로 올려 접은 상태에서 묶음 진척이 읽히게 한다.

const normalizeDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

// parentId 가 서로를 가리키는 옛 데이터가 있어도 멈추지 않도록 지나온
// 작업을 기억해 다시 들어가지 않는다.
export const rollUpHierarchy = (task, ancestors = new Set()) => {
  const key = String(task.id)
  const source = ancestors.has(key) ? [] : (task.children || [])
  const nextAncestors = new Set(ancestors).add(key)
  const children = source.map((child) => rollUpHierarchy(child, nextAncestors))
  if (!children.length) return { ...task, children: [], isGroup: false }

  const dates = [task.startDate, task.endDate, ...children.flatMap((child) => [child.startDate, child.endDate])]
    .map(normalizeDate)
    .filter(Boolean)
  return {
    ...task,
    children,
    isGroup: true,
    startDate: dates.length ? new Date(Math.min(...dates.map((date) => date.getTime()))) : task.startDate,
    endDate: dates.length ? new Date(Math.max(...dates.map((date) => date.getTime()))) : task.endDate,
    progress: Math.round(children.reduce((sum, child) => sum + Number(child.progress || 0), 0) / children.length),
  }
}

// 부모 사슬을 거슬러 올라가 자기에게 돌아오면 순환이다. 순환에 걸린
// 작업들은 어디에도 최상위가 없어, 그냥 붙이면 그 가지가 화면에서 통째로
// 사라진다(목록에도, 타임라인에도 안 나온다). 최상위로 올려 보이게 한다.
const hasCycle = (tasks, task) => {
  const byId = new Map(tasks.map((item) => [String(item.id), item]))
  const self = String(task.id)
  const seen = new Set([self])
  let cursor = byId.get(String(task.parentId))
  while (cursor) {
    const key = String(cursor.id)
    if (key === self) return true
    if (seen.has(key)) return false
    seen.add(key)
    cursor = cursor.parentId != null ? byId.get(String(cursor.parentId)) : null
  }
  return false
}

// 평면 작업 목록을 계층 트리로 세운다.
// id 는 데이터에 숫자/문자가 섞여 들어온다. 키를 문자로 고정하지 않으면
// 부모를 못 찾아 자식이 전부 최상위로 튄다.
export const buildHierarchyRows = (tasks) => {
  const map = new Map(tasks.map((task) => [String(task.id), { ...task, children: [] }]))
  const roots = []
  tasks.forEach((task) => {
    const current = map.get(String(task.id))
    const parent = task.parentId != null ? map.get(String(task.parentId)) : null
    if (parent && parent !== current && !hasCycle(tasks, task)) parent.children.push(current)
    else roots.push(current)
  })
  return roots.map((task) => rollUpHierarchy(task))
}
